type AnalyticsPayload = {
  name: string;
  path?: string;
  pagePath?: string;
  targetUrl?: string;
  eventPublicId?: string;
  sponsorSlot?: string;
  label?: string;
  hasImage?: string;
};

type BeforeInstallPromptEvent = Event & {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
};

function sendAnalytics(payload: AnalyticsPayload): void {
  const body = JSON.stringify({
    path: window.location.pathname + window.location.search,
    pagePath: window.location.pathname,
    ...payload
  });
  const blob = new Blob([body], { type: "application/json" });
  if (navigator.sendBeacon?.("/api/analytics/event", blob)) return;
  void fetch("/api/analytics/event", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body,
    keepalive: true
  }).catch(() => undefined);
}

function linkEventName(link: HTMLAnchorElement): string | null {
  const explicit = link.dataset.analyticsClick;
  if (explicit === "whatsapp") return "whatsapp_click";
  if (explicit === "contact") {
    const href = link.href.toLowerCase();
    if (href.includes("google.com/maps") || href.includes("maps.app.goo.gl") || href.includes("waze.com")) return "map_click";
    if (href.includes("api.whatsapp.com") || href.includes("wa.me")) return "whatsapp_click";
    return "contact_click";
  }
  if (explicit === "sponsor") return "sponsor_click";
  const href = link.href.toLowerCase();
  if (href.includes("api.whatsapp.com") || href.includes("wa.me")) return "whatsapp_click";
  return null;
}

function eventIdFor(link: HTMLAnchorElement): string {
  return link.dataset.analyticsEventId || link.closest<HTMLElement>("[data-analytics-event-id]")?.dataset.analyticsEventId || "";
}

function attachClickTracking(): void {
  document.addEventListener("click", (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    const link = target.closest<HTMLAnchorElement>("a[href]");
    if (!link) return;
    const name = linkEventName(link);
    if (!name) return;
    sendAnalytics({
      name,
      targetUrl: link.href,
      eventPublicId: eventIdFor(link),
      sponsorSlot: link.dataset.analyticsSponsor || "",
      label: link.dataset.analyticsLabel || link.textContent?.trim() || ""
    });
  });
}

function attachSponsorImpressions(): void {
  const sponsors = Array.from(document.querySelectorAll<HTMLElement>("[data-analytics-sponsor]"));
  if (!sponsors.length) return;
  const seen = new WeakSet<HTMLElement>();
  const send = (element: HTMLElement) => {
    if (seen.has(element)) return;
    seen.add(element);
    sendAnalytics({
      name: "sponsor_impression",
      sponsorSlot: element.dataset.analyticsSponsor || "",
      hasImage: element.dataset.analyticsHasImage || "0"
    });
  };

  if (!("IntersectionObserver" in window)) {
    sponsors.forEach(send);
    return;
  }

  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting || entry.intersectionRatio < 0.5) return;
        const element = entry.target;
        if (element instanceof HTMLElement) {
          send(element);
          observer.unobserve(element);
        }
      });
    },
    { threshold: [0.5] }
  );
  sponsors.forEach((element) => observer.observe(element));
}

function installPwa(): void {
  if ("serviceWorker" in navigator) {
    void navigator.serviceWorker.register("/service-worker.js").catch(() => undefined);
  }

  const banner = document.querySelector<HTMLElement>("#pwa-install-banner");
  const installButton = document.querySelector<HTMLButtonElement>("#pwa-install-button");
  const dismissButton = document.querySelector<HTMLButtonElement>("#pwa-install-dismiss");
  const message = document.querySelector<HTMLElement>("#pwa-install-message");
  if (!banner || !installButton || !dismissButton || !message) return;

  const dismissedKey = "agenda-do-vale-pwa-install-dismissed-v1";
  const isStandalone = window.matchMedia("(display-mode: standalone)").matches || Boolean((navigator as Navigator & { standalone?: boolean }).standalone);
  if (isStandalone || localStorage.getItem(dismissedKey)) return;

  const hide = (remember: boolean) => {
    if (remember) localStorage.setItem(dismissedKey, "1");
    banner.hidden = true;
  };
  const isIos = /iPad|iPhone|iPod/.test(navigator.userAgent);
  let deferredPrompt: BeforeInstallPromptEvent | null = null;

  if (isIos) {
    message.textContent = "No iPhone, toque em Compartilhar e depois em “Adicionar à Tela de Início”.";
    installButton.hidden = true;
    banner.hidden = false;
  }

  window.addEventListener("beforeinstallprompt", (event) => {
    event.preventDefault();
    deferredPrompt = event as BeforeInstallPromptEvent;
    banner.hidden = false;
  });
  window.addEventListener("appinstalled", () => hide(false));

  installButton.addEventListener("click", () => {
    if (!deferredPrompt) return;
    void (async () => {
      await deferredPrompt?.prompt();
      await deferredPrompt?.userChoice;
      deferredPrompt = null;
      hide(false);
    })();
  });
  dismissButton.addEventListener("click", () => hide(true));
}

attachClickTracking();
attachSponsorImpressions();
installPwa();
